import { useEffect, useRef } from "react";
import { API_LOANS, API_LOANS_DURATIONS } from "../../constants/apis";
import {
  BANK_ACCOUNT,
  CASH_AT_AGENT,
  COMPONENT,
  FILE,
  FORMDATA,
  LOANS,
  MOBILE_MONEY,
  SELECT,
  TEXT,
} from "../../constants/constants";
import { PropOptions } from "../../utils/formUtils";
import {
  addSelectOption,
  addTagOption,
  currencyFormat,
} from "../../utils/helpers";
import useGet from "../common/useGet";
import useAxios from "../common/useAxios";
import { showConfetti } from "../../components/elements/ConfettiView";
import { InfoBox } from "../../components/elements/Elements";
import { INFO_YELLOW } from "../../constants/colors";
import { reloadData } from "../../components/lists/ListHandler";
import { CalculatorCard } from "../../components/elements/CardElements";
import {
  DocumentOutline,
  ImageOutline,
} from "@raresail/react-ionicons";
import styled from "styled-components";
import { PRIMARY_COLOR, primary10 } from "../../constants/colors";
import { alpha } from "@mui/material";

// ─── DocUploadField ──────────────────────────────────────────────

interface DocUploadFieldProps {
  label: string;
  helperText: string;
  accept: string;
  icon: any;
  value?: File | null;
  onChange: (file: File | null) => void;
  error?: string;
  required?: boolean;
}

const DocUploadField = ({
  label,
  helperText,
  accept,
  icon: Icon,
  value,
  onChange,
  error,
  required,
}: DocUploadFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasFile = !!value;

  return (
    <DocFieldWrapper hasFile={hasFile} hasError={!!error}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          onChange(file);
          e.target.value = "";
        }}
      />

      <DocIconCircle hasFile={hasFile}>
        <Icon fontSize={20} />
      </DocIconCircle>

      <DocContent>
        <div className="font13 semiBold textDark" style={{ lineHeight: 1.3 }}>
          {label}
          {required && (
            <span className="red" style={{ marginLeft: 3 }}>
              *
            </span>
          )}
        </div>
        {hasFile ? (
          <div className="font11 main semiBold" style={{ marginTop: 2 }}>
            ✓ {(value as File).name}
          </div>
        ) : (
          <div className="font11 grey" style={{ marginTop: 2 }}>
            {helperText}
          </div>
        )}
      </DocContent>

      <DocActionButton
        type="button"
        hasFile={hasFile}
        onClick={() =>
          hasFile ? onChange(null) : inputRef.current?.click()
        }
      >
        {hasFile ? "Remove" : "Browse"}
      </DocActionButton>

      {error && <DocError className="font11 red">{error}</DocError>}
    </DocFieldWrapper>
  );
};

// ─── Styled components ───────────────────────────────────────────

const DocFieldWrapper = styled.div<{ hasFile?: boolean; hasError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1.5px solid
    ${({ hasFile, hasError }) =>
      hasError ? "#ff4d4f" : hasFile ? PRIMARY_COLOR : "#e8edf5"};
  background: ${({ hasFile }) =>
    hasFile ? alpha(PRIMARY_COLOR, 0.03) : "#fafbfc"};
  transition: border-color 0.2s ease, background 0.2s ease;
  flex-wrap: wrap;

  &:hover {
    border-color: ${({ hasError, hasFile }) =>
      hasError
        ? "#ff4d4f"
        : hasFile
        ? PRIMARY_COLOR
        : alpha(PRIMARY_COLOR, 0.45)};
  }
`;

const DocIconCircle = styled.div<{ hasFile?: boolean }>`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ hasFile }) => (hasFile ? "#fff" : PRIMARY_COLOR)};
  background: ${({ hasFile }) => (hasFile ? PRIMARY_COLOR : primary10)};
  transition: all 0.2s ease;
`;

const DocContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocActionButton = styled.button<{ hasFile?: boolean }>`
  padding: 5px 14px;
  border-radius: 6px;
  border: 1.5px solid
    ${({ hasFile }) => (hasFile ? "#ff4d4f" : PRIMARY_COLOR)};
  color: ${({ hasFile }) => (hasFile ? "#ff4d4f" : PRIMARY_COLOR)};
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ hasFile }) =>
      hasFile ? alpha("#ff4d4f", 0.06) : alpha(PRIMARY_COLOR, 0.06)};
  }
`;

const DocError = styled.div`
  width: 100%;
  margin-top: 2px;
`;

const DocSectionHeader = styled.div`
  padding: 4px 0 6px;
  border-bottom: 1px solid #f0f2f7;
`;

// ─── Hook ────────────────────────────────────────────────────────

export const useLoansForm = ({ form, updateFormValues, error }: any) => {
  const interest_rate = form?.interest_rate || 0;
  const interest_amount = form?.amount * (interest_rate / 100) || 0;
  const total_amount = form?.amount + interest_amount || 0;

  const {
    loadData: getLoanDurations,
    data: loanDurations,
    loading: loadLoanDurations,
  } = useGet();

  const { set: axiosSet } = useAxios();

  // ✅ FIX: Guard against onSuccess firing more than once (React StrictMode /
  // form-library double-invocation). Reset on every fresh mount so the guard
  // doesn't block a legitimate second submission in the same session.
  const uploadedRef = useRef(false);
  useEffect(() => {
    uploadedRef.current = false;
  }, []);

  useEffect(() => {
    getLoanDurations({ api: API_LOANS_DURATIONS });
  }, []);

  const uploadDocument = (
    loanId: string,
    file: File,
    documentType: string
  ): Promise<void> =>
    new Promise((resolve) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", documentType);
      formData.append("loan", loanId);

      axiosSet({
        api: `${API_LOANS}${loanId}/upload-document/`,
        methodName: "POST",
        type: FORMDATA,
        params: formData,
        successMessage: false,
        successHandler: () => resolve(),
        errorHandler: () => resolve(),
      });
    });

  const fields = [
    {
      label: "loan_amount",
      field: "amount",
      type: TEXT,
      required: true,
      props: { ...PropOptions.number, prefix: "UGX" },
    },
    {
      field: "loan_duration",
      type: SELECT,
      required: true,
      options: loanDurations?.map((val: any) =>
        addTagOption(val, {
          tag: "interest_rate",
          suffix: "%",
          label: "label",
          value: "value",
        })
      ),
      optionKeys: ["interest_rate"],
      props: { loading: loadLoanDurations },
    },
    {
      label: "purpose_of_loan",
      field: "purpose",
      type: SELECT,
      required: true,
      options: [
        "home_improvement",
        "education",
        "medical_expenses",
        "business_expansion",
        "debt_consolidation",
        "vehicle_purchase",
        "agricultural_investment",
        "emergency_expenses",
        "wedding_or_event_expenses",
        "vacation_or_travel",
        "rent_or_mortgage_payments",
        "technology_or_electronics_purchase",
        "investment",
        "business_start_up_capital",
        "other",
      ].map((v) => addSelectOption(v)),
    },
    {
      field: "payment_method",
      type: SELECT,
      required: true,
      options: [MOBILE_MONEY, BANK_ACCOUNT, CASH_AT_AGENT].map((v) =>
        addSelectOption(v)
      ),
    },
    {
      field: "repayment_frequency",
      type: SELECT,
      required: true,
      options: ["daily", "weekly", "monthly"].map((v) =>
        addSelectOption(v)
      ),
    },
    {
      type: COMPONENT,
      visible: interest_rate !== 0 && total_amount !== 0,
      component: (
        <CalculatorCard
          title="Loan Calculator"
          fields={{
            rate: `${interest_rate}%`,
            interest: currencyFormat(interest_amount),
            total_amount: currencyFormat(total_amount),
          }}
        />
      ),
    },

    // ── Supporting documents ─────────────────────────────────
    {
      type: COMPONENT,
      component: (
        <DocSectionHeader>
          <div className="font14 semiBold textDark">Supporting Documents</div>
          <div className="font12 grey" style={{ marginTop: 3 }}>
            Required for assessment. PDF, JPG or PNG accepted. Max 10MB each.
          </div>
        </DocSectionHeader>
      ),
    },
    {
      type: COMPONENT,
      component: (
        <DocUploadField
          label="Mobile Money Statement"
          helperText="Last 3 months — PDF or image"
          accept=".pdf,.png,.jpg,.jpeg"
          icon={DocumentOutline}
          value={form?.mobile_money_statement}
          onChange={(file) =>
            updateFormValues({ mobile_money_statement: file })
          }
          error={error?.mobile_money_statement}
          required
        />
      ),
    },
    {
      field: "mobile_money_statement",
      type: FILE,
      visible: false,
      required: true,
      notValid: !form?.mobile_money_statement
        ? "Please upload your mobile money statement"
        : null,
    },
    {
      type: COMPONENT,
      component: (
        <DocUploadField
          label="Business Stock Photo"
          helperText="Clear photo of your business inventory"
          accept=".png,.jpg,.jpeg,.pdf"
          icon={ImageOutline}
          value={form?.stock_photo}
          onChange={(file) => updateFormValues({ stock_photo: file })}
          error={error?.stock_photo}
          required
        />
      ),
    },
    {
      field: "stock_photo",
      type: FILE,
      visible: false,
      required: true,
      notValid: !form?.stock_photo
        ? "Please upload a photo of your business stock"
        : null,
    },
    // ID document is NOT requested here — it's part of account-level KYC
    // (id_front + id_back, uploaded on the Account page). The route guard
    // (IsKYCComplete) blocks loan submission until KYC is done, so by the
    // time we reach this form the user's ID is already on file.
    {
      type: COMPONENT,
      visible: form?.payment_method != null,
      component: (
        <InfoBox color={INFO_YELLOW}>
          I agree to the <b>Terms and Conditions</b> of the loan agreement and
          confirm that all information provided is accurate and complete.
        </InfoBox>
      ),
    },
  ];

  const onSubmit = ({ payload }: any) => {
    const momoFile: File | null = payload.mobile_money_statement ?? null;
    const stockFile: File | null = payload.stock_photo ?? null;

    const loanPayload = { ...payload };
    delete loanPayload.mobile_money_statement;
    delete loanPayload.stock_photo;

    return {
      api: API_LOANS,
      params: loanPayload,
      successMessage: { message: "Loan application submitted!", duration: 7 },
      onSuccess: async ({ id }: { id: string }) => {
        // ✅ FIX: Bail out if this callback has already run once for this
        // submission (guards against React StrictMode / double-invocation).
        if (uploadedRef.current) return;
        uploadedRef.current = true;

        if (momoFile) await uploadDocument(id, momoFile, "mobile_money_statement");
        if (stockFile) await uploadDocument(id, stockFile, "stock_photo");
        // ID is reused from account-level KYC — see comment in field list.

        reloadData(LOANS);
        showConfetti();
      },
    };
  };

  return { fields, onSubmit, title: "Apply for Loan" };
};